import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CalendarDays, Loader2, Video } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function groupByDay(slots, lang) {
  const fmt = new Intl.DateTimeFormat(lang, { weekday: "short", month: "short", day: "numeric" });
  const groups = [];
  slots.forEach((iso) => {
    const d = new Date(iso);
    const key = d.toDateString();
    let g = groups.find((x) => x.key === key);
    if (!g) groups.push((g = { key, label: fmt.format(d), slots: [] }));
    g.slots.push(iso);
  });
  return groups;
}

const mentorCheckout = async (start, note) => {
  const { data: r } = await api.post("/mentor-booking/checkout", { start, note });
  window.location.href = r.url;
};

export function SlotPicker({ open, onOpenChange, onConfirm = mentorCheckout, title, confirmKey = "mentorBooking.payFor", showHold = true, showNote = true }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const [data, setData] = useState(null);
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSlot(null);
    api.get("/mentor-booking/slots").then((r) => setData(r.data)).catch(() => setData({ slots: [] }));
  }, [open]);
  const groups = useMemo(() => groupByDay(data?.slots || [], lang), [data, lang]);
  useEffect(() => { if (groups.length && !groups.find((g) => g.key === day)) setDay(groups[0].key); }, [groups, day]);
  const timeFmt = new Intl.DateTimeFormat(lang, { hour: "numeric", minute: "2-digit" });
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const pay = async () => {
    setBusy(true);
    try {
      await onConfirm(slot, note);
      if (onConfirm !== mentorCheckout) { setBusy(false); onOpenChange(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("mentorBooking.error"));
      api.get("/mentor-booking/slots").then((r) => setData(r.data));
      setSlot(null);
      setBusy(false);
    }
  };

  const current = groups.find((g) => g.key === day);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mentor-booking-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title || t("mentorBooking.title")}</DialogTitle>
          <DialogDescription>{t("mentorBooking.subtitle", { tz: tzName })}</DialogDescription>
          <p data-testid="mentor-booking-notice-rule" className="text-xs font-semibold text-amber-700">{t("mentorBooking.noticeRule")}</p>
        </DialogHeader>
        {!data ? <Loader2 className="animate-spin text-slate-400" /> : !groups.length ? (
          <p data-testid="mentor-booking-empty" className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-4">{t("mentorBooking.noSlots")}</p>
        ) : (
          <>
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="mentor-booking-days">
              {groups.map((g) => (
                <button key={g.key} type="button" onClick={() => { setDay(g.key); setSlot(null); }}
                  className={`shrink-0 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${day === g.key ? "bg-blue-900 text-white border-blue-900" : "bg-white border-slate-200 text-slate-700 hover:border-blue-800"}`}>
                  {g.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="mentor-booking-slots">
              {current?.slots.map((iso) => (
                <button key={iso} type="button" onClick={() => setSlot(iso)} data-testid={`mentor-booking-slot-${iso}`}
                  className={`h-10 rounded-lg border text-sm font-semibold transition-colors ${slot === iso ? "bg-amber-500 border-amber-500 text-slate-950" : "bg-white border-slate-200 text-slate-800 hover:border-amber-500"}`}>
                  {timeFmt.format(new Date(iso))}
                </button>
              ))}
            </div>
            {showNote && <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("mentorBooking.notePlaceholder")} data-testid="mentor-booking-note" />}
            {showHold && <p className="text-xs text-slate-500">{t("mentorBooking.holdNote")}</p>}
            <Button disabled={!slot || busy} onClick={pay} data-testid="mentor-booking-pay" className="bg-blue-900 hover:bg-blue-800 h-11">
              {busy && <Loader2 size={14} className="mr-2 animate-spin" />}
              {slot ? t(confirmKey, { when: `${new Date(slot).toLocaleDateString(lang, { month: "short", day: "numeric" })} ${timeFmt.format(new Date(slot))}` }) : t("mentorBooking.pickSlot")}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MentorBookingCard({ item, isPremium }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState([]);
  useEffect(() => { api.get("/mentor-booking/me").then((r) => setMine(r.data || [])).catch(() => {}); }, []);
  const upcoming = mine.filter((b) => new Date(b.end) > new Date());
  const fmt = new Intl.DateTimeFormat(i18n.language || "en", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <div className="mt-5 space-y-3" data-testid="mentor-booking-card">
      {upcoming.map((b) => (
        <div key={b.id} data-testid={`mentor-booking-upcoming-${b.id}`} className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm">
          <div className="font-semibold text-emerald-900 flex items-center gap-1.5"><CalendarDays size={14} /> {fmt.format(new Date(b.start))}</div>
          {b.meeting_link
            ? <a href={b.meeting_link} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-emerald-800 font-semibold underline"><Video size={13} /> {t("mentorBooking.joinLink")}</a>
            : <div className="mt-1 text-emerald-800 text-xs">{t("mentorBooking.linkSoon")}</div>}
        </div>
      ))}
      <Button onClick={() => setOpen(true)} disabled={!isPremium} data-testid="shop-book-mentor-call" className="h-10 w-full bg-blue-800 hover:bg-blue-700 text-white">
        <CalendarDays size={14} className="mr-2" /> {isPremium ? t("mentorBooking.bookCta") : t("mentorBooking.premiumOnly")}
      </Button>
      <SlotPicker open={open} onOpenChange={setOpen} item={item} />
    </div>
  );
}
